import {
    useEffect,
    useRef,
    useState,
} from "react";


const formatTime = (
    timestamp
) => {
    if (
        !timestamp
    ) {
        return "";
    }


    const date =
        new Date(
            timestamp
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }


    return date.toLocaleTimeString();
};


export function GeminiTerminal({
    messages = [],
    onSubmit,
    connected = false,
}) {
    const [
        question,
        setQuestion,
    ] = useState("");


    const [
        busy,
        setBusy,
    ] = useState(false);


    const scrollRef =
        useRef(null);


    useEffect(() => {
        const last =
            messages[
                messages.length -
                    1
            ];


        if (
            last?.status ===
                "COMPLETED" ||
            last?.status ===
                "ERROR"
        ) {
            setBusy(
                false
            );
        }
    }, [
        messages,
    ]);


    useEffect(() => {
        if (
            scrollRef.current
        ) {
            scrollRef.current.scrollTop =
                scrollRef.current
                    .scrollHeight;
        }
    }, [
        messages,
    ]);


    const submit = async (
        event
    ) => {
        event.preventDefault();


        const value =
            question.trim();


        if (
            !value ||
            busy ||
            !connected
        ) {
            return;
        }


        setBusy(
            true
        );


        try {
            await onSubmit?.(
                value
            );


            setQuestion(
                ""
            );
        } catch {
            setBusy(
                false
            );
        }
    };


    return (
        <section className="flex min-h-[520px] flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/80 shadow-2xl shadow-black/10">

            <div className="border-b border-slate-800 px-5 py-4">

                <div className="flex items-center justify-between gap-3">

                    <div>

                        <div className="flex items-center gap-2">

                            <span className="h-2 w-2 rounded-full bg-cyan-300 shadow-lg shadow-cyan-300/40" />

                            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-200">
                                Gemini Terminal
                            </h2>

                        </div>


                        <p className="mt-1 text-xs text-slate-500">
                            Ask about current SMC structure, confluence, or trade rejections.
                        </p>

                    </div>


                    <span className="rounded-full border border-slate-700 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">
                        Analyst only
                    </span>

                </div>

            </div>


            <div
                ref={
                    scrollRef
                }
                className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4"
            >

                {messages.length ===
                    0 && (
                    <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/50 p-5 text-sm leading-6 text-slate-500">

                        <p className="font-medium text-slate-400">
                            Gemini is ready.
                        </p>


                        <p className="mt-2">
                            Try: “Why was the latest BTC setup rejected?” or “What SMC confluence is missing on SOL?”
                        </p>

                    </div>
                )}


                {messages.map(
                    (
                        message
                    ) => {
                        const isUser =
                            message.role ===
                            "user";


                        if (
                            message.status ===
                            "STARTED"
                        ) {
                            return (
                                <div
                                    key={
                                        message.id
                                    }
                                    className="rounded-xl border border-cyan-400/10 bg-cyan-400/5 p-3 text-xs text-cyan-200"
                                >
                                    Gemini is analyzing the current market state…
                                </div>
                            );
                        }


                        return (
                            <div
                                key={
                                    message.id
                                }
                                className={
                                    isUser
                                        ? "ml-6 rounded-xl border border-slate-700 bg-slate-800/70 p-3"
                                        : "mr-3 rounded-xl border border-slate-800 bg-slate-950/70 p-3"
                                }
                            >

                                <div className="mb-1 flex items-center justify-between gap-2">

                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                                        {
                                            isUser
                                                ? "You"
                                                : "Gemini"
                                        }
                                    </span>


                                    <span className="text-[10px] text-slate-700">
                                        {
                                            formatTime(
                                                message.timestamp
                                            )
                                        }
                                    </span>

                                </div>


                                <p className="whitespace-pre-wrap text-xs leading-5 text-slate-300">
                                    {
                                        message.error ??
                                        message.content ??
                                        "No response content."
                                    }
                                </p>


                                {!isUser &&
                                    message.marketContext && (
                                        <p className="mt-3 border-t border-slate-800 pt-2 text-[10px] leading-4 text-slate-600">
                                            Context:{" "}
                                            {
                                                message.marketContext
                                            }
                                        </p>
                                    )}

                            </div>
                        );
                    }
                )}

            </div>


            <form
                onSubmit={
                    submit
                }
                className="border-t border-slate-800 p-4"
            >

                <div className="flex gap-2">

                    <input
                        value={
                            question
                        }
                        onChange={(
                            event
                        ) =>
                            setQuestion(
                                event
                                    .target
                                    .value
                            )
                        }
                        disabled={
                            !connected ||
                            busy
                        }
                        maxLength={
                            2000
                        }
                        placeholder={
                            connected
                                ? "Ask Gemini about the current market…"
                                : "Waiting for dashboard connection…"
                        }
                        className="min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-xs text-slate-200 outline-none transition placeholder:text-slate-700 focus:border-cyan-400/50"
                    />


                    <button
                        type="submit"
                        disabled={
                            !connected ||
                            busy ||
                            !question.trim()
                        }
                        className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-xs font-semibold text-cyan-200 transition hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {
                            busy
                                ? "…"
                                : "Ask"
                        }
                    </button>

                </div>


                <p className="mt-2 text-[10px] text-slate-700">
                    Gemini cannot place, modify, cancel, or authorize trades.
                </p>

            </form>

        </section>
    );
}


export default GeminiTerminal;